import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import ErrorMessage from '@oberoninternal/travelbase-ds/components/form/ErrorMessage';
import { StepperField } from '@oberoninternal/travelbase-ds/components/form/Stepper';
import Modal, { ModalProps } from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Portal from '@reach/portal';
import { Box, Flex } from '@rebass/grid';
import { gql } from '@apollo/client';
import { Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Activity, ActivityCheckoutModalTimeslotFragment, TicketCartItemInput } from '../generated/graphql';
import parseDateTime from '../utils/parseDateTime';
import BookButton from './designsystem/BookButton';
import Stack from './Stack';
import StickyBar from './StickyBar';
import messages from '../constants/messages';

export const fragment = gql`
    fragment ActivityCheckoutModalTimeslot on Timeslot {
        id
        startDateTime
        endDateTime
        allotment
        rateGroup {
            rates {
                id
                name
                price
            }
        }
    }
`;

interface Props extends ModalProps {
    activity: Pick<Activity, 'name'>;
    timeslot: ActivityCheckoutModalTimeslotFragment | null;
    setTicket: (ticket: TicketCartItemInput | null) => Promise<void> | void;
    ticket?: TicketCartItemInput | null;
}

const computeTotalPersons = (values: Record<string, { amount: number }>) =>
    Object.values(values).reduce((total, next) => total + next.amount, 0);

const ActivityCheckoutModal = ({ onClose, open, activity, timeslot, ticket, setTicket }: Props) => {
    const initialValues: Record<string, { amount: number }> = useMemo(
        () =>
            Object.assign(
                {},
                ...(timeslot?.rateGroup?.rates.map(rate => ({
                    [rate.id]: { amount: ticket?.rateLines.find(rateLine => rateLine.rateId === rate.id)?.amount ?? 0 },
                })) ?? [])
            ),
        [ticket, timeslot]
    );
    const intl = useIntl();
    const [removing, setRemoving] = useState(false);

    const onRemove = async (disableIndicator = false) => {
        if (
            !timeslot ||
            !window.confirm(
                intl.formatMessage(
                    {
                        defaultMessage: 'Weet je zeker dat je de {type} uit je mandje wilt verwijderen?',
                    },
                    { type: intl.formatMessage({ defaultMessage: 'activiteit' }) }
                )
            )
        ) {
            return;
        }
        if (!disableIndicator) {
            setRemoving(true);
        }
        await setTicket(null);

        if (!disableIndicator) {
            setRemoving(false);
        }

        onClose();
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={async values => {
                if (!timeslot) {
                    return;
                }
                const persons = computeTotalPersons(values);

                if (!persons) {
                    await onRemove(true);
                    return;
                }

                await setTicket({
                    timeslotId: timeslot.id,
                    rateLines: Object.entries(values)
                        .map(([key, value]) => ({
                            rateId: key,
                            amount: value.amount,
                        }))
                        .filter(rateLine => !!rateLine.amount),
                });
                onClose();
            }}
        >
            {({ resetForm, values, dirty, isSubmitting, submitForm }) => {
                if (!Object.keys(values).length) {
                    return null;
                }
                const persons = computeTotalPersons(values);

                const ticketBarContent = (
                    <>
                        {ticket && (
                            <Box mr={3}>
                                <Button
                                    disabled={isSubmitting}
                                    submitting={removing}
                                    onClick={() => onRemove()}
                                    variant="danger"
                                >
                                    Verwijderen
                                </Button>
                            </Box>
                        )}
                        {typeof timeslot?.allotment === 'number' && persons >= timeslot.allotment && (
                            <Box mr={3}>
                                <ErrorMessage>
                                    {intl.formatMessage(
                                        {
                                            defaultMessage:
                                                'Er {allotment, plural, one {is slechts #} other {zijn slechts #} } beschikbaar',
                                        },
                                        { allotment: timeslot.allotment }
                                    )}
                                </ErrorMessage>
                            </Box>
                        )}
                        {!ticket && (
                            <ButtonGroup>
                                <BookButton
                                    onClick={submitForm}
                                    disabled={!dirty || removing}
                                    type="submit"
                                    submitting={isSubmitting}
                                >
                                    {intl.formatMessage(messages.buyTickets)}
                                </BookButton>
                            </ButtonGroup>
                        )}
                        {ticket && (
                            <ButtonGroup>
                                <BookButton
                                    onClick={() => {
                                        if (dirty) {
                                            submitForm();
                                        } else {
                                            onClose();
                                        }
                                    }}
                                    type="submit"
                                    submitting={isSubmitting}
                                >
                                    <FormattedMessage defaultMessage="Aanpassen" />
                                </BookButton>
                            </ButtonGroup>
                        )}
                    </>
                );
                return (
                    <StyledModal
                        open={open}
                        onClose={() => {
                            if (
                                dirty &&
                                !window.confirm(
                                    intl.formatMessage({
                                        defaultMessage: 'Weet je het zeker? Je selectie gaat verloren.',
                                    })
                                )
                            ) {
                                return;
                            }
                            onClose();
                            resetForm();
                        }}
                        variant="regular"
                    >
                        <ModalHeader>{activity.name}</ModalHeader>
                        {timeslot && (
                            <>
                                <Stack marginTop={4}>
                                    <Body>
                                        <FormattedDate
                                            value={parseDateTime(timeslot.startDateTime)}
                                            format="WEEKDAYMONTHYEAR"
                                        />
                                    </Body>
                                    <Body>
                                        <FormattedTime value={parseDateTime(timeslot.startDateTime)} /> —{' '}
                                        <FormattedTime value={parseDateTime(timeslot.endDateTime)} />
                                    </Body>
                                </Stack>

                                <Stack mt={7} spacing={3}>
                                    {timeslot.rateGroup?.rates.map(rate => {
                                        const total = rate.price * values[rate.id].amount;
                                        return (
                                            <Flex key={rate.id} alignItems="center">
                                                <Flex flex={2} flexDirection={['column', null, 'row']}>
                                                    <Box flex={1}>
                                                        <TicketBody>{rate.name}</TicketBody>
                                                    </Box>
                                                    <Box flex={1}>
                                                        <Body>
                                                            {!rate.price && (
                                                                <FormattedMessage defaultMessage="gratis" />
                                                            )}
                                                            {!!rate.price && (
                                                                <FormattedNumber value={rate.price} format="EUR" />
                                                            )}
                                                        </Body>
                                                    </Box>
                                                </Flex>
                                                <Box flex={1}>
                                                    <StepperField
                                                        editable
                                                        variant="primary"
                                                        name={`['${rate.id}'].amount`}
                                                        maximum={
                                                            (timeslot.allotment ?? 100) -
                                                            persons +
                                                            values[rate.id].amount
                                                        }
                                                    />
                                                </Box>
                                                <Flex flex={1} justifyContent="flex-end">
                                                    <Body>
                                                        <FormattedNumber value={total} format="EUR" />
                                                    </Body>
                                                </Flex>
                                            </Flex>
                                        );
                                    })}
                                    <Flex justifyContent="flex-end">
                                        <Box style={{ textAlign: 'end' }}>
                                            <Body variant="tiny" style={{ fontWeight: 300 }}>
                                                <FormattedMessage defaultMessage="totaal" />
                                            </Body>

                                            <TicketPrice>
                                                <FormattedNumber
                                                    value={
                                                        timeslot.rateGroup?.rates.reduce(
                                                            (acc, next) => acc + next.price * values[next.id].amount,
                                                            0
                                                        ) ?? 0
                                                    }
                                                    format="EUR"
                                                />
                                            </TicketPrice>
                                        </Box>
                                    </Flex>
                                </Stack>
                            </>
                        )}
                        <TicketBar className="gt-s">{ticketBarContent}</TicketBar>

                        {open && (
                            <Portal>
                                <TicketBarMobile className="lt-s">{ticketBarContent}</TicketBarMobile>
                            </Portal>
                        )}
                    </StyledModal>
                );
            }}
        </Formik>
    );
};

export default ActivityCheckoutModal;

const ButtonGroup = styled.div`
    > button + button {
        margin-left: ${({ theme }) => theme.spacing['40_Standard']};
    }
    > button {
        flex-shrink: 0;
    }
    display: flex;
    margin-left: auto;
`;

const StyledModal = styled(Modal)`
    [data-reach-dialog-content] {
        display: flex;
        flex-direction: column;
    }

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        [data-reach-dialog-content] {
            padding-bottom: 10rem;
        }
    }
`;

const TicketBarMobile = styled(StickyBar).attrs({ wrapInPortal: true })`
    display: flex;
    width: 100%;
    align-items: center;
    > div > div {
        height: unset;
    }
`;

const TicketBar = styled.div`
    align-items: center;
    padding-top: 2.4rem;
    margin-top: auto;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        margin-top: ${({ theme }) => theme.spacing['60_Large']};
    }
    border-top: 1px solid ${({ theme }) => theme.colors.neutral[20]};
    display: flex;
`;

const TicketBody = styled(Body)`
    font-weight: 400;
`;

const TicketPrice = styled(TicketBody)``;
